import type {
  PatronRenewalEntry,
  PatronRenewalsResponse,
  RenewalBlocker,
  RenewalBlockerCode,
} from "@/api/types";

export type BlockerTone = "muted" | "info" | "warning" | "danger";

/** Short chip label per blocker; the server message carries the detail. */
export const BLOCKER_PRESENTATION: Record<
  RenewalBlockerCode,
  { label: string; tone: BlockerTone }
> = {
  RENEWAL_LIMIT_REACHED: { label: "Déjà prolongé", tone: "muted" },
  RESERVATION_BLOCKING: { label: "Réservé", tone: "warning" },
  TOO_EARLY_TO_RENEW: { label: "Pas encore", tone: "info" },
  RENEWALS_NOT_ALLOWED: { label: "Non prolongeable", tone: "muted" },
  LOAN_NOT_ACTIVE: { label: "Prêt clos", tone: "muted" },
  PATRON_BLOCKED: { label: "Compte bloqué", tone: "danger" },
  PATRON_SUSPENDED: { label: "Compte suspendu", tone: "danger" },
  PATRON_EXPIRED: { label: "Carte expirée", tone: "danger" },
};

const PATRON_BLOCKER_CODES: ReadonlySet<RenewalBlockerCode> = new Set([
  "PATRON_BLOCKED",
  "PATRON_SUSPENDED",
  "PATRON_EXPIRED",
]);

/**
 * Whether the loan can be put in the renewal selection.
 * The override only lifts the blockers the server marked as overridable.
 */
export function isSelectable(entry: PatronRenewalEntry, overrideActive: boolean): boolean {
  return entry.eligibility.canRenew || (overrideActive && entry.eligibility.requiresOverride);
}

/**
 * Blocker explained on the loan row. Account-level blockers are explained once,
 * in the account banner, so a row only falls back to them when nothing else applies.
 */
export function rowBlocker(
  entry: PatronRenewalEntry,
  overrideActive: boolean
): RenewalBlocker | null {
  if (isSelectable(entry, overrideActive)) return null;

  const { blockers } = entry.eligibility;
  return blockers.find((blocker) => !PATRON_BLOCKER_CODES.has(blocker.code)) ?? blockers[0] ?? null;
}

export type AccountGate =
  { kind: "open" } | { kind: "overridable" | "locked"; title: string; detail: string | null };

/**
 * Account status as it affects renewals: blocked and suspended accounts can be
 * overridden by an administrator, an expired card cannot.
 */
export function accountGate(response: PatronRenewalsResponse): AccountGate {
  const { patron, canOverridePatronStatus } = response;

  if (patron.status === "blocked" || patron.status === "suspended") {
    return {
      kind: canOverridePatronStatus ? "overridable" : "locked",
      title: patron.status === "blocked" ? "Compte bloqué" : "Compte suspendu",
      detail: patron.statusReason,
    };
  }

  if (!patron.canBorrow) {
    return {
      kind: "locked",
      title: "Carte expirée",
      detail: "Renouvelez l'inscription du lecteur pour pouvoir prolonger ses prêts.",
    };
  }

  return { kind: "open" };
}

/** Parse a YYYY-MM-DD date as a local calendar day (not UTC midnight). */
export function parseLocalDate(isoDate: string): Date {
  const [year, month, day] = isoDate.slice(0, 10).split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

/** "22 nov.", with the year only when it differs from the reference year. */
export function formatShortDate(isoDate: string, reference: Date = new Date()): string {
  const date = parseLocalDate(isoDate);
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() !== reference.getFullYear() && { year: "numeric" }),
  });
}
